import { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  styled,
  tableCellClasses,
} from "@mui/material";
import {
  useApiGetMunicipalitiesPending,
  useApiUpdateMunicipalityRequest,
} from "../../../../API/municipalities.api";
import {
  MunicipalityChangeRequest,
  MunicipalityRequestStatus,
} from "../../../../API/types/municipalities.types";
import { useNotificationStore } from "../../../../Zustand/notification.store";
import MunicipalitiesTableSkeleton from "./MunicipalitiesTableSkeleton";
import MunicipalitiesTableRow from "./MunicipalitiesTableRow";
import {
  useApiCreateState,
  useApiDeleteStateMunicipality,
  useApiPutStateMunicipality,
} from "../../../../API/documentationCatalogs.api";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#003766",
    color: theme.palette.primary.contrastText,
    fontWeight: "bold",
    position: "sticky",
    top: 0,
    zIndex: 1,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
  border: "1px solid #e0e0e0",
}));

const MunicipalitiesTable = () => {
  /**
   * Custom hooks
   */
  const showNotification = useNotificationStore((s) => s.show);

  /**
   * States
   */
  const [municipalityRequests, setMunicipalityRequests] = useState<
    MunicipalityChangeRequest[]
  >([]);
  const [approvalStates, setApprovalStates] = useState<
    Record<string, MunicipalityRequestStatus>
  >({});

  /**
   * API
   */
  // 🟣 API - Get municipalities
  const {
    call: callGetMunicipalitiesPending,
    called: getMunicipalitiesPendingCalled,
    loading: getMunicipalitiesPendingLoading,
  } = useApiGetMunicipalitiesPending({
    onCompleted: (res) => {
      if (res.data) {
        setMunicipalityRequests(res.data);
        if (!getMunicipalitiesPendingCalled) {
          // Initialize approval states with the current status from API
          const initialApprovalStates: Record<
            string,
            MunicipalityRequestStatus
          > = {};
          res.data.forEach((municipality) => {
            initialApprovalStates[municipality.id] = municipality.status;
          });
          setApprovalStates(initialApprovalStates);
        }
      } else setMunicipalityRequests([]);
    },
  });

  // 🟣 API - Update municipality status
  const { call: callUpdateMunicipalityRequest } =
    useApiUpdateMunicipalityRequest({
      onCompleted: (res) => {
        callGetMunicipalitiesPending();
        showNotification(`Municipality request ${res.data.status}`);
      },
    });

  // 🟣 API - Create state
  const { call: callCreateState, loading: createStateLoading } =
    useApiCreateState();

  // 🟣 API - Create or update municipality
  const {
    call: callPutStateMunicipality,
    loading: putStateMunicipalityLoading,
  } = useApiPutStateMunicipality();

  // 🟣 API - Delete municipality
  const {
    call: callDeleteStateMunicipality,
    loading: deleteStateMunicipalityLoading,
  } = useApiDeleteStateMunicipality();

  /**
   * Callbacks
   */
  // 🟤 Cb - Change approval status for row
  const handleApprovalChange = (
    id: string,
    value: MunicipalityRequestStatus
  ) => {
    setApprovalStates((prev) => ({
      ...prev,
      [id]: value,
    }));
  };

  const handleActionClick = async (
    id: string,
    action: MunicipalityRequestStatus,
    row: MunicipalityChangeRequest
  ) => {
    // Remove a municipality
    if (
      action === "accepted" &&
      row.municipalityId &&
      row.action === "delete"
    ) {
      await callDeleteStateMunicipality(row.municipalityId)
        .then(() => {
          callUpdateMunicipalityRequest(id, { status: action });
        })
        .catch(() =>
          showNotification("Municipality could not be deleted", "error")
        );
      return;
    }

    let stateId: number | undefined = row.stateId;

    // Create state
    if (action === "accepted" && row.isState) {
      try {
        const res = await callCreateState({
          name: row.stateName,
          abbreviation: row.stateName,
        });
        stateId = res?.data?.stateId;
      } catch (error) {
        showNotification("State could not be created", "error");
        return;
      }
    }

    // Create or update a municipality
    if (
      action === "accepted" &&
      row.specType &&
      row.websiteUrl &&
      stateId &&
      row.requestedMunicipalityName !== ""
    ) {
      await callPutStateMunicipality({
        municipality_name: row.requestedMunicipalityName,
        spec_filters: row.specFilters ?? [],
        spec_type: row.specType,
        state_id: stateId,
        website_url: row.websiteUrl,
      })
        .then(() => {
          callUpdateMunicipalityRequest(id, { status: action });
        })
        .catch(() =>
          showNotification(`Municipality could not be ${row.action}d`, "error")
        );
      return;
    }

    // Reject request
    await callUpdateMunicipalityRequest(id, { status: action });
  };

  /**
   * Effects
   */
  // 🟡 Effect - Get municipalities pending on mount
  useEffect(() => {
    callGetMunicipalitiesPending();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <TableContainer
      component={Paper}
      sx={{
        maxHeight: 600,
        width: "100%",
        "& .MuiTable-root": {
          tableLayout: "fixed",
        },
      }}
    >
      <Table stickyHeader aria-label="municipalities table">
        <TableHead>
          <TableRow>
            <StyledTableCell sx={{ width: "5%" }}>ID</StyledTableCell>
            <StyledTableCell sx={{ width: "11%" }}>User</StyledTableCell>
            <StyledTableCell sx={{ width: "12%" }}>State</StyledTableCell>
            <StyledTableCell sx={{ width: "15%" }}>
              Requested Municipality
            </StyledTableCell>
            <StyledTableCell sx={{ width: "10%" }}>
              Requested Change
            </StyledTableCell>
            <StyledTableCell sx={{ width: "18%" }}>Spec Filter</StyledTableCell>
            <StyledTableCell sx={{ width: "14%" }}>Spec Type</StyledTableCell>
            <StyledTableCell sx={{ width: "16%" }}>Website URL</StyledTableCell>
            <StyledTableCell sx={{ width: "17%" }}>Comments</StyledTableCell>
            <StyledTableCell sx={{ width: "13%" }}>Approval</StyledTableCell>
            <StyledTableCell sx={{ width: "10%" }}>Action</StyledTableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {getMunicipalitiesPendingLoading ||
            putStateMunicipalityLoading ||
            createStateLoading ||
            deleteStateMunicipalityLoading ? (
            <MunicipalitiesTableSkeleton />
          ) : (
            municipalityRequests.map((row) => (
              <MunicipalitiesTableRow
                key={row.id}
                row={row}
                approvalState={approvalStates[row.id] || "pending"}
                onApprovalChange={handleApprovalChange}
                onActionClick={handleActionClick}
              />
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default MunicipalitiesTable;
